// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import buildOptionsKey from 'app/common/redux/reducers/resources/buildOptionsKey';
import logStats from 'app/common/lib/logStats';
import PropTypes from 'prop-types';
import type { Action } from 'app/common/redux/actions/types';
import type { State as Resources } from 'app/common/redux/reducers/resources';
import typeof { resources } from 'node/resources/routes';
import { resourceFetch, resourceFetchMore } from 'app/common/redux/actions/resourceFetch';

type ResourceName = $Keys<resources>;

// NOTE: JSON.stringify doesn't sort keys.
const deepEquals = (obj1: Object, obj2: Object) => JSON.stringify(obj1) === JSON.stringify(obj2);

type OptionsOrThunk = Object | ((props: Object) => Object);
type Resource = {
  name: $Keys<resources>,
  options: OptionsOrThunk,
  key: string,
};

type WithResourceOptions = {
  allowStale?: false,
  // traceLoggerHandlerName signals that we should create a subspan for this
  // resource if this traceLogger is active
  traceLoggerHandlerName?: string,
};

export function buildOptionsOrThunkKey(options: OptionsOrThunk, props: Object) {
  return buildOptionsKey(typeof options === 'function' ? options(props) : options || {});
}

export const getMapStateToProps = (Subject?: React.ComponentType<*>) => {
  const didReadResource = {};

  return (resources: Resource[]) => (state: { resources: Resources }, props: Object) => {
    /*
     * Map store state to withResourceLegacy's props.
     * https://github.com/reactjs/react-redux/blob/master/docs/api.md
     */

    const { fetching: fetchingState, data: dataState } = state.resources;

    const isFetching = resources.some(
      ({ name, options }) =>
        fetchingState[name] && fetchingState[name][buildOptionsOrThunkKey(options, props)],
    );

    const resourceData: Object = {};
    let resourceError;
    const bookmarks = [];
    resources.forEach(({ name, options, key }) => {
      if (dataState[name]) {
        const optionsKey = buildOptionsOrThunkKey(options, props);

        // Only log if it's the first time to read this resource.
        // Subsequent data fetches within the same session will all probably have
        // some data in here, and once we're on the next page it's all from ajax
        // responses anyway. Logging more is noisey.
        if (!didReadResource[name]) {
          if (!(optionsKey in dataState[name])) {
            if (Object.keys(dataState[name]).length) {
              logStats.increment('webapp.withResource.populated-cache-miss', 0.05, {
                resource: name,
                cache_size: Object.keys(dataState[name]).length,
                level: 'error',
                v: 1,
                xFoundKeys: JSON.stringify(Object.keys(dataState[name])),
                xExpectedKey: optionsKey,
              });
            } else {
              logStats.increment('webapp.withResource.empty-cache-miss', 0.05, {
                resource: name,
                cache_size: Object.keys(dataState[name]).length,
                level: 'warn',
                v: 1,
                xFoundKeys: null,
                xExpectedKey: optionsKey,
              });
            }
          } else {
            logStats.increment('webapp.withResource.cache-hit', 0.05, {
              resource: name,
              cache_size: Object.keys(dataState[name]).length,
              level: 'success',
              v: 1,
            });
          }
        }
        didReadResource[name] = true;

        if (dataState[name][optionsKey]) {
          const { data, nextBookmark, error } = dataState[name][optionsKey];
          resourceData[key] = data;
          bookmarks.push(nextBookmark);
          if (error) {
            resourceError = error;
          }
        }
      } else {
        logStats.increment('webapp.withResource.no-cache', 0.05, {
          resource: name,
          cache_size: 0,
          level: 'error',
          v: 1,
        });
      }
    });
    const resourceLoaded = resources.every(({ key }) => resourceData[key] !== undefined);
    const isAtEnd =
      resourceLoaded && !isFetching && bookmarks.every((bookmark) => bookmark === '-end-');
    const nextBookmark = bookmarks[0]; // NOTE: this really only makes sense when bookmarks.length === 1.

    return {
      resourceLoaded,
      isAtEnd,
      isFetching,
      nextBookmark,
      resourceData,
      error: resourceError,
    };
  };
};

type ResourceProps = {
  dispatch: (action: Action) => void,
  error: mixed,
  isAtEnd: boolean,
  isFetching: boolean,
  nextBookmark: string,
  onResourceError?: (error: mixed) => void,
  onResourceLoaded?: () => void,
  resourceData: Object,
  resourceLoaded: boolean,
};
export const buildResource = (resources: Resource[], options: WithResourceOptions = {}) => (
  Subject: React.ComponentType<*>,
) =>
  class extends React.Component<ResourceProps, *> {
    static WrappedComponent = Subject;

    static displayName =
      resources.length === 1 ? resources[0].name : `${resources.length} resources`;

    static propTypes = {
      dispatch: PropTypes.func.isRequired,
      // eslint-disable-next-line react/forbid-prop-types
      error: PropTypes.any,
      isFetching: PropTypes.bool.isRequired,
      onResourceError: PropTypes.func,
      onResourceLoaded: PropTypes.func,
      resourceData: PropTypes.objectOf(PropTypes.any).isRequired,
      resourceLoaded: PropTypes.bool.isRequired,
    };

    static defaultProps: *;

    componentDidMount() {
      if (options.allowStale !== false) {
        // Check if stale data is present
        if (!this.props.resourceLoaded) {
          this.loadData(this.props);
        }
      } else {
        this.loadData(this.props);
      }
    }

    shouldComponentUpdate(nextProps: ResourceProps) {
      // Do a deep comparison of resourceData, and a shallow comparison for everything else
      const keys = [...Object.keys(this.props), ...Object.keys(nextProps)];
      for (const key of keys) {
        if (key === 'resourceData') {
          if (!deepEquals(this.props.resourceData, nextProps.resourceData)) {
            return true;
          }
        } else if (this.props[key] !== nextProps[key]) {
          return true;
        }
      }
      return false;
    }

    UNSAFE_componentWillUpdate(nextProps: ResourceProps) {
      // Callback for sending resourceLoaded state to parent components.
      if (!this.props.resourceLoaded && nextProps.resourceLoaded) {
        if (this.props.onResourceLoaded) {
          this.props.onResourceLoaded();
        }
      }
      if (!this.props.error && nextProps.error) {
        if (this.props.onResourceError) {
          this.props.onResourceError(nextProps.error);
        }
      }

      // Reload data if the props changed.
      const nextResourcesHash = JSON.stringify(
        resources.map((resource) => ({
          ...resource,
          options: buildOptionsOrThunkKey(resource.options, nextProps),
        })),
      );

      if (this.resourcesHash !== nextResourcesHash) {
        this.loadData(nextProps);
        this.resourcesHash = nextResourcesHash;
      }
    }

    loadData = (props: ResourceProps) => {
      // Needed because local variable `options` will collide with destructured
      // variable inside the `forEach` function
      const withResourceOptions = options;
      resources.forEach(({ options, name }) => {
        const finalOptions = typeof options === 'function' ? options(props) : options;
        props.dispatch(
          resourceFetch(
            name,
            finalOptions,
            undefined,
            undefined,
            withResourceOptions.traceLoggerHandlerName,
          ),
        );
      });
    };

    loadMoreData = (props: ResourceProps) => {
      // Needed because local variable `options` will collide with destructured
      // variable inside the `forEach` function
      const withResourceOptions = options;
      resources.forEach(({ options, name }) => {
        const finalOptions = typeof options === 'function' ? options(props) : options;
        props.dispatch(
          resourceFetchMore(
            name,
            finalOptions,
            undefined,
            undefined,
            withResourceOptions.traceLoggerHandlerName,
          ),
        );
      });
    };

    resourcesHash = JSON.stringify(
      resources.map((resource) => ({
        ...resource,
        options: buildOptionsOrThunkKey(resource.options, this.props),
      })),
    );

    render() {
      const { error, isAtEnd, isFetching, resourceData, resourceLoaded, ...props } = this.props;

      delete props.onResourceError;
      delete props.onResourceLoaded;

      return (
        <Subject
          {...resourceData}
          {...props}
          error={error}
          fetchMoreData={() => this.loadMoreData(this.props)}
          isAtEnd={isAtEnd}
          isFetching={isFetching}
          refreshData={() => this.loadData(this.props)}
          resourceLoaded={resourceLoaded}
        />
      );
    }
  };

export type incompleteResource = {
  name: *,
  options?: OptionsOrThunk,
  key?: string,
};
const withResourceLegacy = (resource: incompleteResource, options: WithResourceOptions = {}) => (
  Subject: React.ComponentType<*>,
) => {
  const completeResource: Resource = {
    name: resource.name,
    options: resource.options || {},
    key: resource.key || 'data',
  };

  // Return a component that gets resource data from the redux store.
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  return connect(getMapStateToProps(Subject)([completeResource]))(
    buildResource([completeResource], options)(Subject),
  );
};

export const withResource = withResourceLegacy;
export default withResourceLegacy;

export const mapStateToResourceData = ({
  name,
  options,
}: {
  name: ResourceName,
  options: OptionsOrThunk,
}) => (state: { resources: Resources }, props: Object) => {
  const { data: dataState } = state.resources;
  if (dataState[name]) {
    const optionsKey = buildOptionsOrThunkKey(options, props);

    if (dataState[name][optionsKey]) {
      const { data } = dataState[name][optionsKey];
      return { data };
    }
  }
  return { data: null };
};

export function withStaleResourceData({
  name,
  options,
}: {
  name: ResourceName,
  options?: OptionsOrThunk,
}) {
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  return connect(mapStateToResourceData({ name, options: options || {} }));
}
